import { useState, useEffect } from 'react';
import api from '../../api';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  ChevronLeft, ChevronRight, 
  Monitor, CheckCircle, AlertTriangle, XCircle,
  ChevronUp, ChevronDown, ChevronsUpDown, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const AgentesPage = () => {
  const [agentes, setAgentes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, page_size: 25, total: 0, pages: 0 });
  const [stats, setStats] = useState({ total: 0, activos: 0, advertencia: 0, criticos: 0, versiones: [] });
  
  // Sorting
  const [sortConfig, setSortConfig] = useState({ field: 'ultimo_reporte', dir: 'desc' });
  
  // Column filters
  const [columnFilters, setColumnFilters] = useState({
    search: '',
    version: '',
    estado: '',
  });

  // Sort handler
  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sort icon component
  const SortIcon = ({ field }) => {
    if (sortConfig.field !== field) {
      return <ChevronsUpDown className="h-3 w-3 text-muted-foreground/50" />;
    }
    return sortConfig.dir === 'asc' 
      ? <ChevronUp className="h-3 w-3 text-primary" />
      : <ChevronDown className="h-3 w-3 text-primary" />;
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/config/agentes/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchAgentes = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        page_size: pagination.page_size.toString(),
        sort_by: sortConfig.field,
        sort_dir: sortConfig.dir,
      });
      
      if (columnFilters.search) params.append('search', columnFilters.search);
      if (columnFilters.version && columnFilters.version !== 'all') params.append('version_filter', columnFilters.version);
      if (columnFilters.estado && columnFilters.estado !== 'all') params.append('estado_filter', columnFilters.estado);

      const response = await api.get(`/config/agentes?${params}`);
      setAgentes(response.data.items);
      setPagination(prev => ({ ...prev, total: response.data.total, pages: response.data.pages }));
    } catch (error) {
      toast.error('Error al cargar agentes');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchAgentes();
  }, [pagination.page, sortConfig]);
  
  // Debounced filter effect
  useEffect(() => {
    const t = setTimeout(() => {
      setPagination(prev => ({ ...prev, page: 1 }));
      fetchAgentes();
    }, 800);
    return () => clearTimeout(t);
  }, [columnFilters]);

  const handleFilterChange = (field, value) => {
    setColumnFilters(prev => ({ ...prev, [field]: value }));
  };

  // Quick filter by clicking stats
  const handleStatFilter = (estado) => {
    setColumnFilters(prev => ({ ...prev, estado: estado }));
  };

  // Format date helper
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('es-AR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get badge class based on estado
  const getEstadoBadge = (estado) => {
    switch (estado) {
      case 'activo':
        return { class: 'badge-success', label: 'Activo', icon: CheckCircle };
      case 'advertencia':
        return { class: 'badge-warning', label: '>24h', icon: AlertTriangle };
      case 'critico':
        return { class: 'badge-error', label: '>7 días', icon: XCircle };
      default:
        return { class: 'badge-default', label: '-', icon: Monitor };
    }
  };

  const handleRefresh = () => {
    fetchStats();
    fetchAgentes();
    toast.success('Datos actualizados');
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">Estado de Agentes</h1>
            <p className="page-subtitle">Monitoreo de agentes instalados en workstations</p>
          </div>
          <Button onClick={handleRefresh} variant="outline" className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Actualizar
          </Button>
        </div>

        {/* Stats - Clickeable para filtrar */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="agentes-stats">
          <div 
            className={`stats-card cursor-pointer transition-all hover:ring-2 hover:ring-primary/50 ${columnFilters.estado === '' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleStatFilter('')}
            data-testid="stats-total"
          >
            <div className="mb-2">
              <Monitor className="h-8 w-8 text-primary" strokeWidth={1.5} />
            </div>
            <div className="stats-card-value">{stats.total}</div>
            <div className="stats-card-label">Total Agentes</div>
          </div>
          
          <div 
            className={`stats-card cursor-pointer transition-all hover:ring-2 hover:ring-green-500/50 ${columnFilters.estado === 'activo' ? 'ring-2 ring-green-500' : ''}`}
            onClick={() => handleStatFilter('activo')}
            data-testid="stats-activos"
          >
            <div className="mb-2">
              <CheckCircle className="h-8 w-8 text-green-500" strokeWidth={1.5} />
            </div>
            <div className="stats-card-value text-green-500">{stats.activos}</div>
            <div className="stats-card-label">Activos (hoy)</div>
          </div>
          
          <div 
            className={`stats-card cursor-pointer transition-all hover:ring-2 hover:ring-yellow-500/50 ${columnFilters.estado === 'advertencia' ? 'ring-2 ring-yellow-500' : ''}`}
            onClick={() => handleStatFilter('advertencia')}
            data-testid="stats-advertencia"
          >
            <div className="mb-2">
              <AlertTriangle className="h-8 w-8 text-yellow-500" strokeWidth={1.5} />
            </div>
            <div className="stats-card-value text-yellow-500">{stats.advertencia}</div>
            <div className="stats-card-label">Advertencia (&gt;24h)</div>
          </div>
          
          <div 
            className={`stats-card cursor-pointer transition-all hover:ring-2 hover:ring-red-500/50 ${columnFilters.estado === 'critico' ? 'ring-2 ring-red-500' : ''}`}
            onClick={() => handleStatFilter('critico')}
            data-testid="stats-criticos"
          >
            <div className="mb-2">
              <XCircle className="h-8 w-8 text-red-500" strokeWidth={1.5} />
            </div>
            <div className="stats-card-value text-red-500">{stats.criticos}</div>
            <div className="stats-card-label">Críticos (&gt;7 días)</div>
          </div>
        </div>

        {/* Table */}
        <Card className="card-subtle overflow-hidden">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="table-subtle">
                    <thead>
                      {/* Headers Row */}
                      <tr>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('nombre_pc')}
                        >
                          <div className="flex items-center gap-1">
                            EQUIPO
                            <SortIcon field="nombre_pc" />
                          </div>
                        </th>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('version_agente')}
                        >
                          <div className="flex items-center gap-1">
                            VERSIÓN
                            <SortIcon field="version_agente" />
                          </div>
                        </th>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('ultimo_reporte')}
                        >
                          <div className="flex items-center gap-1">
                            ÚLTIMO REPORTE
                            <SortIcon field="ultimo_reporte" />
                          </div>
                        </th>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('ultima_actualizacion')}
                        >
                          <div className="flex items-center gap-1">
                            ÚLTIMA ACTUALIZACIÓN
                            <SortIcon field="ultima_actualizacion" />
                          </div>
                        </th>
                        <th className="text-center">ESTADO</th>
                      </tr>
                      {/* Filters Row */}
                      <tr className="bg-muted/30">
                        <th className="py-2 px-4">
                          <Input
                            placeholder="Buscar PC..."
                            value={columnFilters.search}
                            onChange={(e) => handleFilterChange('search', e.target.value)}
                            className="h-7 text-xs bg-background"
                            data-testid="filter-search"
                          />
                        </th>
                        <th className="py-2 px-4">
                          <Select value={columnFilters.version || "all"} onValueChange={(v) => handleFilterChange('version', v)}>
                            <SelectTrigger className="h-7 text-xs bg-background">
                              <SelectValue placeholder="Todas" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">Todas</SelectItem>
                              {stats.versiones?.map(v => (
                                <SelectItem key={v} value={v}>{v}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </th>
                        <th className="py-2 px-4"></th>
                        <th className="py-2 px-4"></th>
                        <th className="py-2 px-4">
                          <Select value={columnFilters.estado || "all"} onValueChange={(v) => handleFilterChange('estado', v === "all" ? "" : v)}>
                            <SelectTrigger className="h-7 text-xs bg-background">
                              <SelectValue placeholder="Todos" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">Todos</SelectItem>
                              <SelectItem value="activo">Activo (hoy)</SelectItem>
                              <SelectItem value="advertencia">Advertencia (&gt;24h)</SelectItem>
                              <SelectItem value="critico">Crítico (&gt;7 días)</SelectItem>
                            </SelectContent>
                          </Select>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {agentes.length === 0 ? (
                        <tr>
                          <td colSpan={5}>
                            <div className="empty-state">
                              <Monitor className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                              <p className="empty-state-text">No se encontraron agentes</p>
                            </div>
                          </td>
                        </tr>
                      ) : agentes.map((agent) => {
                        const estadoBadge = getEstadoBadge(agent.estado);
                        const IconComponent = estadoBadge.icon;
                        return (
                          <tr key={agent.id} data-testid={`agent-row-${agent.id}`}>
                            <td className="font-medium text-primary">{agent.nombre_pc}</td>
                            <td>
                              <span className="badge badge-info">{agent.version_agente}</span>
                            </td>
                            <td className="text-muted-foreground text-sm">
                              {formatDate(agent.ultimo_reporte)}
                            </td>
                            <td className="text-muted-foreground text-sm">
                              {formatDate(agent.ultima_actualizacion)}
                            </td>
                            <td className="text-center">
                              <span className={`badge ${estadoBadge.class} inline-flex items-center gap-1`}>
                                <IconComponent className="h-3 w-3" />
                                {estadoBadge.label}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                  <div className="text-sm text-muted-foreground">
                    {pagination.total > 0 ? `${(pagination.page - 1) * pagination.page_size + 1} - ${Math.min(pagination.page * pagination.page_size, pagination.total)} de ${pagination.total}` : '0 registros'}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPagination(p => ({ ...p, page: Math.max(1, p.page - 1) }))}
                      disabled={pagination.page === 1}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-muted-foreground">{pagination.page} / {pagination.pages || 1}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPagination(p => ({ ...p, page: Math.min(p.pages, p.page + 1) }))}
                      disabled={pagination.page >= pagination.pages}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default AgentesPage;
